// Leaderboard — the world-class version. All 13 features from the /admin
// upgrade request:
//   1. time-window chips (24h / 7d / 30d / 90d)
//   2. sortable columns (click header)
//   3. filter chips (community, post_type, has_flags, has_editor_action)
//   4. free-text search (debounced)
//   5. per-row 24h sparkline
//   6. trend indicators (↑ %, ↓ %) vs prev window
//   7. anomaly badges (brigade, silent-hit, N flags)
//   8. bulk actions toolbar (select multiple, delete / suspend authors)
//   9. saved views (localStorage)
//  10. real-time refresh toggle (30s poll)
//  11. author aggregation view (toggle between Posts / Authors)
//  12. CSV export
//  13. big-3 hero cards above the table

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api';
import { BarSparkline, Sparkline } from './Sparkline';

// ---- Types ----

type TimeWindow = '24h' | '7d' | '30d' | '90d';
type Sort   = 'score' | 'recommends' | 'comments' | 'flags' | 'reads' | 'recent';
type Dir    = 'desc' | 'asc';
type View   = 'posts' | 'authors';

interface PostRow {
    id: string; title: string; post_type: string;
    author: string; author_id: string;
    community_slug: string;
    score: number; engaged_readers: number;
    recommends: number; comments: number; flags_open: number;
    created_at: string;
    sparkline_24h?: number[];
    prev_score?: number;
    trend_pct?: number;
    anomalies?: string[];
}
interface AuthorRow {
    author_id: string; name: string; email: string;
    score: number; posts: number;
    unique_readers: number; flags_open: number;
}
interface Facets {
    communities: Array<{ slug: string; name: string }>;
    post_types: string[];
}
interface SavedView {
    id: string; label: string;
    window: TimeWindow; sort: Sort; dir: Dir; view: View;
    community?: string; post_type?: string;
    has_flags?: boolean; has_editor_action?: boolean;
    q?: string;
}

// ---- Component ----

interface Props {
    onInspect: (userID: string) => void;
}

export function Leaderboard({ onInspect }: Props) {
    const api = useApi();
    const navigate = useNavigate();

    // View state (all URL-syncable if we want that later; localStorage for now)
    const [view, setView] = useState<View>('posts');
    const [window, setWindow]         = useState<TimeWindow>('7d');
    const [sort, setSort]             = useState<Sort>('score');
    const [dir, setDir]               = useState<Dir>('desc');
    const [community, setCommunity]   = useState<string>('');
    const [postType, setPostType]     = useState<string>('');
    const [hasFlags, setHasFlags]     = useState(false);
    const [hasEditorAction, setHasEditorAction] = useState(false);
    const [q, setQ]                   = useState<string>('');
    const [qDebounced, setQDebounced] = useState<string>('');
    const [offset, setOffset]         = useState(0);
    const [expanded, setExpanded]     = useState<string | null>(null);
    const [selected, setSelected]     = useState<Set<string>>(new Set());
    const [autoRefresh, setAutoRefresh] = useState(false);

    // Data state
    const [posts, setPosts]     = useState<PostRow[]>([]);
    const [authors, setAuthors] = useState<AuthorRow[]>([]);
    const [total, setTotal]     = useState(0);
    const [facets, setFacets]   = useState<Facets>({ communities: [], post_types: [] });

    // Saved views (localStorage)
    const [views, setViews]     = useState<SavedView[]>(() => {
        try { return JSON.parse(localStorage.getItem('onhere_admin_views') || '[]'); }
        catch { return []; }
    });

    const limit = 20;

    // Debounce free-text search — 400ms after last keystroke.
    useEffect(() => {
        const t = setTimeout(() => setQDebounced(q), 400);
        return () => clearTimeout(t);
    }, [q]);

    const load = useCallback(async () => {
        try {
            if (view === 'posts') {
                const params = new URLSearchParams({
                    window, sort, dir, limit: String(limit), offset: String(offset),
                    with_sparklines: '1', with_prev: '1',
                });
                if (community) params.set('community', community);
                if (postType)  params.set('post_type', postType);
                if (hasFlags)  params.set('has_flags', '1');
                if (hasEditorAction) params.set('has_editor_action', '1');
                if (qDebounced) params.set('q', qDebounced);
                const r = await api.get<{ posts: PostRow[]; total_matched: number }>(
                    `/api/admin/posts-leaderboard?${params.toString()}`);
                setPosts(r.posts ?? []);
                setTotal(r.total_matched ?? 0);
            } else {
                const r = await api.get<{ authors: AuthorRow[] }>(
                    `/api/admin/authors-leaderboard?window=${window}&limit=${limit}&offset=${offset}`);
                setAuthors(r.authors ?? []);
                setTotal(0); // authors endpoint doesn't return total yet
            }
            const f = await api.get<Facets>(`/api/admin/leaderboard-facets?window=${window}`);
            setFacets(f);
        } catch (e) { console.error('leaderboard load', e); }
    }, [api, view, window, sort, dir, community, postType, hasFlags, hasEditorAction, qDebounced, offset]);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh — 30s poll when toggle is on.
    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(load, 30_000);
        return () => clearInterval(id);
    }, [autoRefresh, load]);

    // ---- Actions ----

    const toggleSort = (key: Sort) => {
        if (sort === key) { setDir(dir === 'desc' ? 'asc' : 'desc'); }
        else              { setSort(key); setDir('desc'); }
        setOffset(0);
    };

    const toggleSelect = (id: string) => {
        const s = new Set(selected);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSelected(s);
    };

    const clearFilters = () => {
        setCommunity(''); setPostType(''); setHasFlags(false); setHasEditorAction(false);
        setQ(''); setOffset(0);
    };

    const saveCurrentView = () => {
        const label = globalThis.prompt('Name this view:');
        if (!label) return;
        const v: SavedView = {
            id: crypto.randomUUID(), label,
            window, sort, dir, view,
            community: community || undefined, post_type: postType || undefined,
            has_flags: hasFlags || undefined, has_editor_action: hasEditorAction || undefined,
            q: qDebounced || undefined,
        };
        const next = [...views, v];
        setViews(next);
        try { localStorage.setItem('onhere_admin_views', JSON.stringify(next)); } catch { /* private mode */ }
    };
    const loadView = (v: SavedView) => {
        setView(v.view); setWindow(v.window); setSort(v.sort); setDir(v.dir);
        setCommunity(v.community ?? ''); setPostType(v.post_type ?? '');
        setHasFlags(!!v.has_flags); setHasEditorAction(!!v.has_editor_action);
        setQ(v.q ?? ''); setOffset(0);
    };
    const deleteView = (id: string) => {
        const next = views.filter(v => v.id !== id);
        setViews(next);
        try { localStorage.setItem('onhere_admin_views', JSON.stringify(next)); } catch { /* ignore */ }
    };

    const exportCSV = () => {
        const rows = view === 'posts' ? posts : authors;
        if (rows.length === 0) return;
        const keys = Object.keys(rows[0]).filter(k => k !== 'sparkline_24h' && k !== 'anomalies');
        const lines = [
            keys.join(','),
            ...rows.map(r => keys.map(k => csvCell((r as unknown as Record<string, unknown>)[k])).join(',')),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `onhere-${view}-${window}-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    async function bulkAction(kind: 'force-delete' | 'suspend-author') {
        const reason = globalThis.prompt(`${kind} selected — reason?`);
        if (!reason) return;
        const ids = Array.from(selected);
        for (const id of ids) {
            const row = posts.find(p => p.id === id);
            if (!row) continue;
            try {
                if (kind === 'force-delete') {
                    await api.del(`/api/admin/posts/${id}`);
                } else {
                    await api.post(`/api/admin/users/${row.author_id}/suspend`, { reason });
                }
            } catch (e) { console.warn(`${kind} failed for ${id}`, e); }
        }
        setSelected(new Set());
        load();
    }

    // Hero cards — top 3 posts always the first 3 of the current view.
    const heroes = useMemo(() => view === 'posts' ? posts.slice(0, 3) : [], [view, posts]);
    const rest   = useMemo(() => view === 'posts' ? posts.slice(3) : [], [view, posts]);

    return (
        <section style={{ marginTop: 32 }}>
            {/* Header + view switcher + toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
                    <h2 style={{ margin: 0 }}>
                        {view === 'posts' ? 'Posts' : 'Authors'} leaderboard
                    </h2>
                    <span style={{ fontSize: 12, color: '#888' }}>· {total > 0 ? `${total} match` : `${posts.length + authors.length} shown`}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <ChipGroup value={view} onChange={setView} options={['posts','authors']} />
                    <ChipGroup value={window} onChange={(w) => { setWindow(w); setOffset(0); }} options={['24h','7d','30d','90d']} />
                    <label style={{ fontSize: 11, color: '#666', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} /> auto-refresh 30s
                    </label>
                    <button type="button" className="plain" onClick={load} style={smallBtn}>refresh</button>
                    <button type="button" className="plain" onClick={exportCSV} style={smallBtn}>export CSV</button>
                    <button type="button" className="plain" onClick={saveCurrentView} style={smallBtn}>save view</button>
                </div>
            </div>

            {/* Saved views */}
            {views.length > 0 && (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>saved:</span>
                    {views.map(v => (
                        <span key={v.id} style={savedViewChip}>
                            <button type="button" style={savedViewBtn} onClick={() => loadView(v)}>{v.label}</button>
                            <button type="button" style={savedViewX}   onClick={() => deleteView(v.id)} title="delete">×</button>
                        </span>
                    ))}
                </div>
            )}

            {/* Filter chips (posts view only) */}
            {view === 'posts' && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                    <select value={community} onChange={e => { setCommunity(e.target.value); setOffset(0); }} style={filterSelect}>
                        <option value="">All communities</option>
                        {facets.communities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                    <select value={postType} onChange={e => { setPostType(e.target.value); setOffset(0); }} style={filterSelect}>
                        <option value="">All kinds</option>
                        {facets.post_types.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <label style={{ fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <input type="checkbox" checked={hasFlags} onChange={e => { setHasFlags(e.target.checked); setOffset(0); }} />
                        has open flags
                    </label>
                    <label style={{ fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <input type="checkbox" checked={hasEditorAction} onChange={e => { setHasEditorAction(e.target.checked); setOffset(0); }} />
                        has editor action
                    </label>
                    <input type="search" placeholder="Search title / body…"
                           value={q} onChange={e => { setQ(e.target.value); setOffset(0); }}
                           style={{ ...filterSelect, minWidth: 200 }} />
                    <button type="button" className="plain" onClick={clearFilters} style={smallBtn}>clear filters</button>
                </div>
            )}

            {/* Bulk actions toolbar (posts view, when selection exists) */}
            {view === 'posts' && selected.size > 0 && (
                <div style={bulkBar}>
                    <span>{selected.size} selected</span>
                    <button type="button" style={dangerBtn} onClick={() => bulkAction('force-delete')}>Force-delete posts</button>
                    <button type="button" style={dangerBtn} onClick={() => bulkAction('suspend-author')}>Suspend authors</button>
                    <button type="button" className="plain" onClick={() => setSelected(new Set())} style={smallBtn}>clear</button>
                </div>
            )}

            {/* Hero cards for top 3 (posts view only) */}
            {view === 'posts' && heroes.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 12 }}>
                    {heroes.map((p, i) => (
                        <div key={p.id} style={heroCard} onClick={() => navigate(`/posts/${p.id}`)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>#{i + 1} · {p.community_slug}</span>
                                {p.anomalies && p.anomalies.length > 0 && (
                                    <span style={anomalyBadge}>⚠ {p.anomalies[0]}</span>
                                )}
                            </div>
                            <div style={{ fontFamily: "'Newsreader',serif", fontSize: 18, margin: '4px 0', color: 'var(--accent)' }}>
                                {p.title}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                                <span style={{ fontSize: 28, fontWeight: 600 }}>{p.score}</span>
                                {p.trend_pct !== undefined && (
                                    <TrendBadge pct={p.trend_pct} />
                                )}
                                {p.sparkline_24h && (
                                    <span style={{ marginLeft: 'auto' }}>
                                        <BarSparkline data={p.sparkline_24h} width={80} height={24} color="#5a4a1a" />
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                                {p.engaged_readers} reads · {p.recommends} recs · {p.comments} comments
                                {p.flags_open > 0 && <span style={{ color: '#8b1919' }}> · {p.flags_open} flags</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Table */}
            {view === 'posts' ? (
                <PostsTable
                    rows={rest} offset={offset + 3}
                    sort={sort} dir={dir} onSort={toggleSort}
                    selected={selected} onSelect={toggleSelect}
                    onRowClick={(id) => navigate(`/posts/${id}`)}
                    onAuthorClick={(userID) => onInspect(userID)}
                    expanded={expanded} onExpand={(id) => setExpanded(expanded === id ? null : id)}
                />
            ) : (
                <AuthorsTable rows={authors} offset={offset} onAuthorClick={onInspect} />
            )}

            {/* Pagination */}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#666' }}>
                    Showing {offset + 1}–{offset + (view === 'posts' ? posts.length : authors.length)}
                    {total > 0 ? ` of ${total}` : ''}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="plain" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}>← prev</button>
                    <button type="button" className="plain" onClick={() => setOffset(offset + limit)} disabled={(view === 'posts' ? posts.length : authors.length) < limit}>next →</button>
                </div>
            </div>
        </section>
    );
}

// ---- Subcomponents ----

interface PostsTableProps {
    rows: PostRow[]; offset: number;
    sort: Sort; dir: Dir; onSort: (s: Sort) => void;
    selected: Set<string>; onSelect: (id: string) => void;
    onRowClick: (id: string) => void;
    onAuthorClick: (userID: string) => void;
    expanded: string | null; onExpand: (id: string) => void;
}

function PostsTable({ rows, offset, sort, dir, onSort, selected, onSelect, onRowClick, onAuthorClick, expanded, onExpand }: PostsTableProps) {
    if (rows.length === 0) {
        return <p style={{ color: '#888', fontStyle: 'italic', marginTop: 12 }}>No more posts.</p>;
    }
    return (
        <table style={tableStyle}>
            <thead>
                <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                    <th style={th}></th>
                    <th style={th}>#</th>
                    <th style={th}>Post</th>
                    <th style={th}>Community</th>
                    <th style={th}>Author</th>
                    <SortHeader label="Score"    active={sort==='score'}      dir={dir} onClick={() => onSort('score')} />
                    <SortHeader label="Reads"    active={sort==='reads'}      dir={dir} onClick={() => onSort('reads')} />
                    <SortHeader label="Recs"     active={sort==='recommends'} dir={dir} onClick={() => onSort('recommends')} />
                    <SortHeader label="Comments" active={sort==='comments'}   dir={dir} onClick={() => onSort('comments')} />
                    <SortHeader label="Flags"    active={sort==='flags'}      dir={dir} onClick={() => onSort('flags')} />
                    <th style={th}>24h</th>
                    <th style={th}>Trend</th>
                    <th style={th}></th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <>
                        <tr key={r.id} style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: selected.has(r.id) ? '#fff8e1' : undefined }}
                            onClick={() => onRowClick(r.id)}>
                            <td style={th}>
                                <input type="checkbox" checked={selected.has(r.id)}
                                       onClick={e => e.stopPropagation()}
                                       onChange={() => onSelect(r.id)} />
                            </td>
                            <td style={td}>{offset + i + 1}</td>
                            <td style={{ ...td, color: 'var(--accent)' }}>
                                {r.title.slice(0, 50)}
                                <span style={{ marginLeft: 6, fontSize: 10, color: '#888', textTransform: 'uppercase' }}>{r.post_type}</span>
                                {r.anomalies && r.anomalies.length > 0 && (
                                    <span style={anomalyBadge}>⚠ {r.anomalies.join(' · ')}</span>
                                )}
                            </td>
                            <td style={td}>{r.community_slug}</td>
                            <td style={td}>
                                <button type="button" style={inspectBtn} onClick={(e) => { e.stopPropagation(); onAuthorClick(r.author_id); }}>
                                    {r.author}
                                </button>
                            </td>
                            <td style={{ ...td, fontWeight: 600 }}>{r.score}</td>
                            <td style={td}>{r.engaged_readers}</td>
                            <td style={td}>{r.recommends}</td>
                            <td style={td}>{r.comments}</td>
                            <td style={{ ...td, color: r.flags_open > 0 ? '#8b1919' : '#333' }}>{r.flags_open || '—'}</td>
                            <td style={td}>{r.sparkline_24h && <BarSparkline data={r.sparkline_24h} width={70} height={18} />}</td>
                            <td style={td}>{r.trend_pct !== undefined && <TrendBadge pct={r.trend_pct} />}</td>
                            <td style={td}>
                                <button type="button"
                                        title="Quick stats without leaving the page"
                                        style={chevronBtn}
                                        onClick={(e) => { e.stopPropagation(); onExpand(r.id); }}>
                                    {expanded === r.id ? '▴' : '▾'}
                                </button>
                            </td>
                        </tr>
                        {expanded === r.id && <ExpandedRow key={r.id + '-x'} row={r} />}
                    </>
                ))}
            </tbody>
        </table>
    );
}

function AuthorsTable({ rows, offset, onAuthorClick }: { rows: AuthorRow[]; offset: number; onAuthorClick: (id: string) => void }) {
    if (rows.length === 0) return <p style={{ color: '#888', fontStyle: 'italic', marginTop: 12 }}>No authors in this window.</p>;
    return (
        <table style={tableStyle}>
            <thead>
                <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                    <th style={th}>#</th>
                    <th style={th}>Author</th>
                    <th style={th}>Score</th>
                    <th style={th}>Posts</th>
                    <th style={th}>Unique readers</th>
                    <th style={th}>Open flags</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={r.author_id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={td}>{offset + i + 1}</td>
                        <td style={td}>
                            <button type="button" style={inspectBtn} onClick={() => onAuthorClick(r.author_id)}>
                                {r.name} <span style={{ color: '#888', fontSize: 11 }}>· {r.email}</span>
                            </button>
                        </td>
                        <td style={{ ...td, fontWeight: 600 }}>{r.score}</td>
                        <td style={td}>{r.posts}</td>
                        <td style={td}>{r.unique_readers}</td>
                        <td style={{ ...td, color: r.flags_open > 0 ? '#8b1919' : '#333' }}>{r.flags_open || '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function ExpandedRow({ row }: { row: PostRow }) {
    return (
        <tr style={{ background: '#faf8f2' }}>
            <td colSpan={13} style={{ padding: 12 }}>
                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                    <div>
                        <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase' }}>24h signals</div>
                        {row.sparkline_24h
                            ? <Sparkline data={row.sparkline_24h} width={220} height={40} color="#5a4a1a" fill="#5a4a1a" />
                            : <span style={{ fontSize: 11, color: '#888' }}>—</span>}
                    </div>
                    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                        <div><strong>{row.engaged_readers}</strong> readers engaged</div>
                        <div><strong>{row.recommends}</strong> recommends · <strong>{row.comments}</strong> comments</div>
                        {row.flags_open > 0 && <div style={{ color: '#8b1919' }}><strong>{row.flags_open}</strong> open flags</div>}
                        {row.prev_score !== undefined && (
                            <div>prev window score: <strong>{row.prev_score}</strong> ({row.trend_pct?.toFixed(0)}% change)</div>
                        )}
                        {row.anomalies && row.anomalies.length > 0 && (
                            <div style={{ color: '#8b1919', marginTop: 4 }}>
                                ⚠ {row.anomalies.join(' · ')}
                            </div>
                        )}
                    </div>
                </div>
            </td>
        </tr>
    );
}

function SortHeader({ label, active, dir, onClick }: { label: string; active: boolean; dir: Dir; onClick: () => void }) {
    return (
        <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={onClick}>
            {label} {active && (dir === 'desc' ? '↓' : '↑')}
        </th>
    );
}

function TrendBadge({ pct }: { pct: number }) {
    if (Math.abs(pct) < 5) return <span style={{ fontSize: 10, color: '#888' }}>·</span>;
    const up = pct > 0;
    return (
        <span style={{
            fontSize: 10, padding: '1px 5px', borderRadius: 3,
            background: up ? '#e7f3d7' : '#fadfdf',
            color:      up ? '#4a7020' : '#8b1919',
        }}>
            {up ? '↑' : '↓'} {Math.abs(pct).toFixed(0)}%
        </span>
    );
}

function ChipGroup<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: T[] }) {
    return (
        <div style={{ display: 'inline-flex', border: '1px solid #cfc9b7', borderRadius: 3, overflow: 'hidden' }}>
            {options.map(o => (
                <button key={o} type="button" onClick={() => onChange(o)}
                        style={{
                            padding: '3px 10px', fontSize: 11, cursor: 'pointer', border: 'none',
                            background: o === value ? 'var(--accent)' : '#fff',
                            color:      o === value ? '#fff'          : '#333',
                        }}>
                    {o}
                </button>
            ))}
        </div>
    );
}

// ---- CSV helper + styles ----

function csvCell(v: unknown): string {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
}

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontFamily: '-apple-system, sans-serif', fontSize: 12, marginTop: 12 };
const th: React.CSSProperties = { padding: '6px 8px' };
const td: React.CSSProperties = { padding: '6px 8px' };
const smallBtn: React.CSSProperties = { fontSize: 11, padding: '4px 8px' };
const filterSelect: React.CSSProperties = { padding: '4px 8px', fontSize: 12, fontFamily: '-apple-system, sans-serif' };
const inspectBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, font: 'inherit', textAlign: 'left' };
const chevronBtn: React.CSSProperties = { background: 'none', border: '1px solid #cfc9b7', padding: '2px 8px', cursor: 'pointer', fontSize: 11 };
const dangerBtn: React.CSSProperties = { padding: '4px 10px', border: '1px solid #8b1919', background: '#fff', color: '#8b1919', fontSize: 12, cursor: 'pointer' };
const anomalyBadge: React.CSSProperties = { marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#fadfdf', color: '#8b1919' };
const heroCard: React.CSSProperties = {
    padding: 12, border: '1px solid #cfc9b7', background: '#faf8f2',
    cursor: 'pointer', display: 'flex', flexDirection: 'column',
};
const bulkBar: React.CSSProperties = {
    marginTop: 10, padding: 8, background: '#fff8e1', border: '1px solid #d4a017',
    display: 'flex', gap: 8, alignItems: 'center', fontSize: 12,
};
const savedViewChip: React.CSSProperties = { display: 'inline-flex', border: '1px solid #cfc9b7', borderRadius: 3, overflow: 'hidden' };
const savedViewBtn:  React.CSSProperties = { background: '#fff', border: 'none', padding: '3px 8px', fontSize: 11, cursor: 'pointer' };
const savedViewX:    React.CSSProperties = { background: '#fff', border: 'none', borderLeft: '1px solid #cfc9b7', padding: '3px 6px', fontSize: 11, cursor: 'pointer', color: '#888' };
